import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { InvoiceType } from "../../types/InvoicesTypes";
import api from "../../utils/api";

interface InvoicesState {
  allInvoices: InvoiceType[];
  userInvoices: InvoiceType[];
  loading: boolean;
  error: string | null;
  successMessage: string | null;
}

const initialState: InvoicesState = {
  allInvoices: [],
  userInvoices: [],
  loading: false,
  error: null,
  successMessage: null,
};

// ADD NEW INVOICE
export const addNewInvoice = createAsyncThunk(
  "invoices/addNewInvoice",
  async (invoiceData: any, { rejectWithValue }) => {
    try {
      const res = await api.post(
        `/invoices/add/new`,
        invoiceData,
        { withCredentials: true }
      );
      return res.data.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// FETCH ALL INVOICES
export const fetchAllInvoices = createAsyncThunk(
  "invoices/fetchAllInvoices",
  async (_, { rejectWithValue }) => {
    try {
      const res = await api.get(
        `/invoices/fetch/all`,
        { withCredentials: true }
      );
      return res.data.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// FETCH INVOICES BY LAB
export const fetchInvoicesByLab = createAsyncThunk(
  "invoices/fetchInvoicesByLab",
  async (labToken: string, { rejectWithValue }) => {
    try {
      const res = await api.get(
        `/invoices/fetch/by-lab/${labToken}`,
        { withCredentials: true }
      );
      return res.data.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// UPDATE INVOICE STATUS
export const updateInvoiceStatus = createAsyncThunk(
  "invoices/updateInvoiceStatus",
  async (
    { invoice_Number, status }: { invoice_Number: string; status: string },
    { rejectWithValue }
  ) => {
    try {
      const res = await api.post(
        `/invoices/update/invoice-status`,
        { invoice_Number, status },
        { withCredentials: true }
      );
      return res.data.data; // { invoice_Number, status }
    } catch (err: any) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

const invoicesSlice = createSlice({
  name: "invoices",
  initialState,
  reducers: {
    clearInvoiceState(state) {
      state.error = null;
      state.successMessage = null;
    },
  },

  extraReducers: (builder) => {
    // ADD NEW INVOICE
    builder
      .addCase(addNewInvoice.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(addNewInvoice.fulfilled, (state, action) => {
        state.loading = false;
        state.allInvoices.push(action.payload);
        state.successMessage = "Invoice created successfully";
      })
      .addCase(addNewInvoice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // FETCH ALL INVOICES
    builder
      .addCase(fetchAllInvoices.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllInvoices.fulfilled, (state, action) => {
        state.loading = false;
        state.allInvoices = action.payload;
      })
      .addCase(fetchAllInvoices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // FETCH INVOICES BY LAB
    builder
      .addCase(fetchInvoicesByLab.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchInvoicesByLab.fulfilled, (state, action) => {
        state.loading = false;
        state.userInvoices = action.payload;
      })
      .addCase(fetchInvoicesByLab.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // UPDATE INVOICE STATUS
    builder
      .addCase(updateInvoiceStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateInvoiceStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = "Invoice status updated successfully";

        const { invoice_Number, status } = action.payload;

        // Update both arrays if present
        state.allInvoices = state.allInvoices.map((invoice) =>
          invoice.invoice_Number === invoice_Number
            ? { ...invoice, status }
            : invoice
        );

        state.userInvoices = state.userInvoices.map((invoice) =>
          invoice.invoice_Number === invoice_Number
            ? { ...invoice, status }
            : invoice
        );
      })
      .addCase(updateInvoiceStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearInvoiceState } = invoicesSlice.actions;
export default invoicesSlice.reducer;
